import { fmt, fmtPct, p2Necessaria } from './calculo';
import { ROTULOS, type Componente, type DisciplinaDesempenho } from './types';

export interface Insight {
  tom: 'bom' | 'neutro' | 'atencao';
  titulo: string;
  texto: string;
}

const PARTES_P1: Componente[] = ['T1', 'IND1', 'GRU1'];

/** Frases curtas sobre o desempenho numa disciplina, só com os dados do próprio aluno e agregados da turma. */
export function insightsDisciplina(d: DisciplinaDesempenho): Insight[] {
  const out: Insight[] = [];
  const comp = d.turmaInfo.componentes;
  const p1 = d.notas.P1;

  if (typeof p1 === 'number') {
    const pct = comp.P1?.percentil;
    if (typeof pct === 'number' && pct >= 75) {
      out.push({ tom: 'bom', titulo: 'Entre os melhores', texto: `Sua P1 ficou acima de ${Math.round(pct)}% da turma.` });
    }
    const falta = p2Necessaria(p1);
    out.push(
      falta <= 10
        ? {
            tom: falta <= 6 ? 'bom' : 'atencao',
            titulo: 'Para fechar sem P3',
            texto: `Basta tirar ${fmt(falta)} na P2 para a média (P1 + P2) / 2 chegar a 6.`,
          }
        : {
            tom: 'atencao',
            titulo: 'A P2 sozinha não fecha',
            texto: `Nem com 10 na P2 a média chega a 6 (faltariam ${fmt(falta)}). Com uma boa P2, a P3 substitui a menor nota.`,
          },
    );
  }

  // ponto forte e ponto a melhorar, pela posição na turma em cada parte da P1
  const posicoes = PARTES_P1.map((c) => ({ c, pct: comp[c]?.percentil })).filter(
    (x): x is { c: Componente; pct: number } => typeof x.pct === 'number',
  );
  if (posicoes.length >= 2) {
    const ordenadas = [...posicoes].sort((a, b) => b.pct - a.pct);
    const melhor = ordenadas[0]!;
    const pior = ordenadas[ordenadas.length - 1]!;
    if (melhor.pct - pior.pct >= 15) {
      out.push({
        tom: 'neutro',
        titulo: 'Seu ponto forte',
        texto: `${ROTULOS[melhor.c].longo}: acima de ${Math.round(melhor.pct)}% da turma. Já em ${ROTULOS[pior.c].longo}, acima de ${Math.round(pior.pct)}%.`,
      });
    }
  }

  if (d.awsDisponivel && d.itensT1.length) {
    const corte = d.corteAws ?? 0.7;
    const aprovados = d.itensT1.filter((i) => (i.aluno ?? 0) >= corte - 1e-9).length;
    const faltam = d.itensT1.length - aprovados;
    const pct = Math.round(corte * 100);
    out.push(
      faltam === 0
        ? { tom: 'bom', titulo: 'AWS em dia', texto: `Você atingiu ${pct}% em todos os ${d.itensT1.length} itens da T1.` }
        : {
            tom: 'atencao',
            titulo: `AWS: ${faltam} ${faltam === 1 ? 'item ficou' : 'itens ficaram'} abaixo de ${pct}%`,
            texto: `Cada item com pelo menos ${pct}% vale 10; abaixo disso, 0. Na T2 (prazo 15/11, 23h59), refaça o que for preciso para passar de ${pct}%.`,
          },
    );
  }

  if (d.grupo && !d.grupo.foraDoPrazo && d.requisitos.length) {
    const acima = d.requisitos.filter(
      (r) => r.peso && r.grupo?.nota != null && r.mediaTurma != null && r.grupo.nota / r.peso > r.mediaTurma + 0.05,
    ).length;
    const cheios = d.requisitos.filter((r) => r.peso && r.grupo?.nota != null && r.grupo.nota >= r.peso - 1e-9).length;
    out.push({
      tom: 'neutro',
      titulo: 'Seu grupo na P1',
      texto: `Nota cheia em ${cheios} de ${d.requisitos.length} itens; acima da média da turma em ${acima}.`,
    });
  }
  if (d.grupo?.foraDoPrazo) {
    out.push({
      tom: 'atencao',
      titulo: 'Entrega fora do prazo',
      texto: `A correção do seu grupo daria ${fmt(d.grupo.notaCorrecao)}, mas a nota do grupo registrada é 0 por causa do prazo.`,
    });
  }
  if (!d.grupo) {
    out.push({ tom: 'atencao', titulo: 'P1 sem entrega', texto: 'Não encontrei entrega do seu grupo na P1: o grupo e a individual ficaram com 0.' });
  }
  const ent = d.turmaInfo.p1Entregaram;
  if (ent && d.grupo && !d.grupo.foraDoPrazo && typeof ent.percentil === 'number') {
    out.push({
      tom: 'neutro',
      titulo: 'Só entre quem entregou',
      texto: `Considerando só os ${ent.n} que entregaram a P1 no prazo (média ${fmt(ent.media)}), você fica acima de ${fmtPct(ent.percentil / 100)} deles.`,
    });
  }
  return out;
}
